import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Autocomplete,
  MenuItem
} from '@mui/material';
import { Formik, Field } from 'formik';
import { tokens } from '../../theme';
import useClassService from '../../api/services/ClassService';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ClassSchema } from '../../schemas/classSchema';

const EditDialog = ({ classId, open, onClose, onSuccess }) => {
  const [initialData, setInitialData] = useState(null);
  const { getClassById, updateClassDetails } = useClassService();
  const combinations = ["A-LEVEL", "O-LEVEL"];
  const classTitleOptions = ["FORM-1", "FORM-2", "FORM-3", "FORM-4", "FORM-5", "FORM-6"];

  useEffect(() => {
    const fetchClassData = async () => {
      try {
        const clazz = await getClassById(classId);
        setInitialData({
          id: clazz.id,
          className: clazz.className || "",
          classTitle: clazz.classTitle || "",
          classType: clazz.classType || "",
          classTeacher: clazz.classTeacher || "",
          classTeacherPhoneNumber: clazz.classTeacherPhoneNumber || "",
          dateTime: dayjs()
        });
      } catch (err) {
        console.error("Failed to load class data:", err);
      }
    };

    if (open && classId) fetchClassData();
  }, [open, classId]);

  const handleSave = async (values) => {
    try {
      const payload = {
        id: initialData.id,
        className: values.className,
        classTitle: values.classTitle || null,
        classType: values.classType,
        classTeacher: values.classTeacher,
        classTeacherPhoneNumber: values.classTeacherPhoneNumber
      };

      await updateClassDetails(payload);
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Update failed", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>EDIT CLASS</DialogTitle>
      <DialogContent>
        {initialData && (
          <Formik
            enableReinitialize
            initialValues={initialData}
            validationSchema={ClassSchema}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} mt={1}>

                  {/* CLASS NAME */}
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Class Name"
                      name="className"
                      value={values.className}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.className && !!errors.className}
                      helperText={touched.className && errors.className}
                    />
                  </Grid>

                  {/* CLASS TITLE */}
                  <Grid item xs={12} lg={6}>
                    <TextField
                      select
                      fullWidth
                      variant="filled"
                      label="Class Title"
                      name="classTitle"
                      value={values.classTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.classTitle && !!errors.classTitle}
                      helperText={touched.classTitle && errors.classTitle}
                    >
                      {classTitleOptions.map((title) => (
                        <MenuItem key={title} value={title}>
                          {title}
                        </MenuItem>
                      ))}
                      {values.classTitle && !classTitleOptions.includes(values.classTitle) && (
                        <MenuItem value={values.classTitle}>{values.classTitle}</MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  {/* CLASS TYPE */}
                  <Grid item xs={12} lg={6}>
                    <Field name="classType">
                      {({ field, form }) => (
                        <Autocomplete
                          options={combinations}
                          value={field.value || ""}
                          onChange={(_, newValue) =>
                            form.setFieldValue("classType", newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Class Type"
                              variant="filled"
                              fullWidth
                              error={
                                form.touched.classType &&
                                Boolean(form.errors.classType)
                              }
                              helperText={
                                form.touched.classType &&
                                form.errors.classType
                              }
                            />
                          )}
                        />
                      )}
                    </Field>
                  </Grid>

                  {/* CLASS TEACHER */}
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Class Teacher"
                      name="classTeacher"
                      value={values.classTeacher}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.classTeacher && !!errors.classTeacher}
                      helperText={touched.classTeacher && errors.classTeacher}
                    />
                  </Grid>

                  {/* TEACHER PHONE NUMBER */}
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Teacher Phone Number"
                      name="classTeacherPhoneNumber"
                      value={values.classTeacherPhoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        !!touched.classTeacherPhoneNumber &&
                        !!errors.classTeacherPhoneNumber
                      }
                      helperText={
                        touched.classTeacherPhoneNumber &&
                        errors.classTeacherPhoneNumber
                      }
                    />
                  </Grid>

                  {/* DATE TIME PICKER */}
                  <Grid item xs={12} lg={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        label="Timestamp (optional)"
                        value={values.dateTime}
                        onChange={(newValue) =>
                          setFieldValue("dateTime", newValue)
                        }
                        disableFuture
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>

                <DialogActions sx={{ mt: 2 }}>
                  <Button onClick={onClose} color="secondary" variant="contained">
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditDialog;
